import { SimpleSidebar } from "@volca/ui/components/ui/simple-sidebar";
import { SIDEBAR_SECTIONS } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import AdvocateSidebar from "@/business-settings/customers/advocates/advocate-sidebar";
import LeadSidebar from "@/business-settings/customers/leads/lead-sidebar";
import ReferralPaymentsSidebar from "@/business-settings/payments/referral-payments/referral-payments-sidebar";
import AllReferralsSidebar from "@/business-settings/referral-history/referral-history-sidebar";
import ReferralSourceSidebar from "@/business-settings/referral-sources/referral-source-sidebar";
import { ReviewsSidebar } from "@/business-settings/reviews";
import BusinessSettingsTextMessagesSidebar from "@/business-settings/text-messages/text-messages-sidebar";
import WebAppUserSidebar from "@/business-settings/users/web-app-users/web-app-user-sidebar";
import { IReferral } from "@/referrals/referral";

interface BusinessSettingsSidebarProps {
  businessId: string;
}

interface SidebarContentProps {
  section: string;
  mode: "view" | "new";
  selectedItemId: string | undefined;
  businessId: string;
  isOpen: boolean;
  updateSidebar: (update: { payoutReferral: IReferral }) => void;
  resetSidebar: () => void;
}

const SidebarContent = ({
  section,
  mode,
  selectedItemId,
  businessId,
  isOpen,
  updateSidebar,
  resetSidebar,
}: SidebarContentProps) => {
  switch (section) {
    case SIDEBAR_SECTIONS.referralHistory:
      if (!selectedItemId) return null;
      return (
        <AllReferralsSidebar
          referralId={selectedItemId}
          onClose={resetSidebar}
          onPayout={(referral) => updateSidebar({ payoutReferral: referral })}
        />
      );
    case SIDEBAR_SECTIONS.users:
      return (
        <WebAppUserSidebar
          mode={mode}
          userId={selectedItemId}
          businessId={businessId}
        />
      );
    case SIDEBAR_SECTIONS.leads:
      if (!selectedItemId) return null;
      return <LeadSidebar leadId={selectedItemId} businessId={businessId} />;
    case SIDEBAR_SECTIONS.advocates:
      return (
        <AdvocateSidebar
          mode={mode}
          advocateId={selectedItemId}
          businessId={businessId}
        />
      );
    case SIDEBAR_SECTIONS.referralPayments:
      if (!selectedItemId) return null;
      return (
        <ReferralPaymentsSidebar
          referralPaymentsId={selectedItemId}
          businessId={businessId}
        />
      );
    case SIDEBAR_SECTIONS.textMessages:
      if (!selectedItemId) return null;
      return (
        <BusinessSettingsTextMessagesSidebar
          textMessageId={selectedItemId}
          businessId={businessId}
          isOpen={isOpen}
        />
      );
    case SIDEBAR_SECTIONS.referralSources:
      return (
        <ReferralSourceSidebar
          mode={mode}
          referralSourceId={selectedItemId}
          businessId={businessId}
        />
      );
    case SIDEBAR_SECTIONS.reviews:
      if (!selectedItemId) return null;
      return (
        <ReviewsSidebar reviewId={selectedItemId} onClose={resetSidebar} />
      );
    default:
      return null;
  }
};

export const BusinessSettingsSidebar = ({
  businessId,
}: BusinessSettingsSidebarProps) => {
  const {
    state: { isOpen, mode, section, selectedItemId },
    updateSidebar,
    resetSidebar,
  } = useBusinessSettingsSidebar();

  const isNewMode = mode === "new";
  const isUserPanel = section === SIDEBAR_SECTIONS.users;
  if (!isOpen && !isUserPanel) return <SimpleSidebar />;
  if (!selectedItemId && !isNewMode) return <SimpleSidebar />;

  return (
    <SimpleSidebar>
      <SidebarContent
        section={section}
        mode={mode}
        selectedItemId={selectedItemId}
        businessId={businessId}
        isOpen={isOpen}
        updateSidebar={updateSidebar}
        resetSidebar={resetSidebar}
      />
    </SimpleSidebar>
  );
};
